import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { penaltyAPI, employeeAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export const PENALTY_CATEGORIES = [
  'Late Attendance', 'Missed Deadline', 'SOP Violation', 'Misconduct',
  'Client Complaint', 'Asset Damage', 'Quality Issue', 'Other',
];

export default function PenaltyFormDialog({ open, onOpenChange, penalty, onSaved }) {
  const isEdit = !!penalty;
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      setForm({
        employee_ref_id: penalty?.employee_ref_id || '',
        incident_date: penalty?.incident_date || '',
        category: penalty?.category || '',
        severity: penalty?.severity || 'minor',
        amount: penalty?.amount ?? '',
        description: penalty?.description || '',
        reported_by: penalty?.reported_by || '',
      });
      employeeAPI.getAll().then(res => {
        const list = res.data.employees || res.data || [];
        setEmployees(list.filter(e => e.status !== 'exited' && e.status !== 'terminated'));
      }).catch(() => {});
    }
  }, [open, penalty]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!isEdit && !form.employee_ref_id) {
      toast.error('Please select an employee');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        incident_date: form.incident_date || null,
        category: form.category || null,
        severity: form.severity,
        amount: parseFloat(form.amount) || 0,
        description: form.description || null,
        reported_by: form.reported_by || null,
      };
      if (isEdit) {
        await penaltyAPI.update(penalty.id, payload);
        toast.success('Penalty updated');
      } else {
        await penaltyAPI.create({ ...payload, employee_ref_id: form.employee_ref_id });
        toast.success('Penalty recorded');
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save penalty');
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="penalty-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Penalty' : 'Record Penalty'}</DialogTitle>
          <DialogDescription>
            {isEdit ? `${penalty.employee_name || ''} (${penalty.employee_code || 'unlinked'})` : 'Manually record a disciplinary shortcoming.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {!isEdit && (
            <div>
              <Label className="text-xs">Employee *</Label>
              <Select value={form.employee_ref_id} onValueChange={v => set('employee_ref_id', v)}>
                <SelectTrigger className="mt-1" data-testid="penalty-employee-select">
                  <SelectValue placeholder="Select employee…" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name} {e.employee_id ? `(${e.employee_id})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Incident Date</Label>
              <Input type="date" className="mt-1" value={form.incident_date || ''} onChange={e => set('incident_date', e.target.value)} data-testid="penalty-date-input" />
            </div>
            <div>
              <Label className="text-xs">Severity</Label>
              <Select value={form.severity} onValueChange={v => set('severity', v)}>
                <SelectTrigger className="mt-1" data-testid="penalty-severity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minor">Minor</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="major">Major</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Shortcoming / Category</Label>
              <Select value={form.category} onValueChange={v => set('category', v)}>
                <SelectTrigger className="mt-1" data-testid="penalty-category-select">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {PENALTY_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Penalty Amount (₹)</Label>
              <Input type="number" className="mt-1 font-mono" placeholder="0" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="penalty-amount-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Description</Label>
            <Textarea className="mt-1 min-h-[60px]" rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)} placeholder="What happened?" data-testid="penalty-description-input" />
          </div>

          <div>
            <Label className="text-xs">Reported By</Label>
            <Input className="mt-1" value={form.reported_by || ''} onChange={e => set('reported_by', e.target.value)} placeholder="Manager / reporter name" data-testid="penalty-reporter-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="penalty-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit ? 'Save Changes' : 'Record Penalty'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
